import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Bell, Search, Check, Trash2, X, Filter as FilterIcon,
    AlertCircle, AlertTriangle, Info,
    FileText, Activity, Clock, Gauge, Lock, CreditCard, Link2, Link2Off,
    Calendar, Megaphone,
} from "lucide-react";
import AppShell from "@/components/nav/AppShell";
import { api } from "@/lib/api";

const SEVERITY = {
    critical: { color: "text-error-c",   bg: "bg-error/10",   border: "border-error/30",   Fallback: AlertCircle,    label: "Критично" },
    warning:  { color: "text-warning-c", bg: "bg-warning/10", border: "border-warning/30", Fallback: AlertTriangle,  label: "Попередження" },
    info:     { color: "text-primary-c", bg: "bg-primary/10", border: "border-primary/30", Fallback: Info,           label: "Інфо" },
};
const EVENT_ICONS = {
    report_ready: FileText,              anomaly_detected: Activity,
    weekly_reminder_tomorrow: Clock,     weekly_reminder_overdue: AlertTriangle,
    limit_warning: Gauge,                limit_exhausted: Lock,
    payment_success: CreditCard,         payment_failed: CreditCard,
    mcc_linked: Link2,                   mcc_lost: Link2Off,
    subscription_expiring: Calendar,     product_news: Megaphone,
};
function iconFor(n) {
    return EVENT_ICONS[n.event_type || n.kind] || SEVERITY[n.severity]?.Fallback || Info;
}
function formatRelative(iso) {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "щойно";
    if (diff < 3600) return `${Math.floor(diff / 60)} хв тому`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} год тому`;
    if (diff < 172800) return "вчора";
    return new Date(iso).toLocaleString("uk-UA", { dateStyle: "short", timeStyle: "short" });
}

const PAGE_SIZE = 25;

export default function Notifications() {
    const navigate = useNavigate();
    const [meta, setMeta] = useState(null);              // events catalog from /api/notification-prefs
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [unread, setUnread] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [eventFilter, setEventFilter] = useState([]);  // multi-select event_types
    const [severityFilter, setSeverityFilter] = useState("all");
    const [unreadOnly, setUnreadOnly] = useState(false);
    const [q, setQ] = useState("");
    const [debouncedQ, setDebouncedQ] = useState("");
    const [showFilters, setShowFilters] = useState(false);
    const offsetRef = useRef(0);

    // Catalog for the event-type multi-select.
    useEffect(() => {
        api.get("/notification-prefs")
            .then(r => setMeta(r.data))
            .catch(() => setMeta({ events: [], channels: [] }));
    }, []);

    // Debounce search input.
    useEffect(() => {
        const t = setTimeout(() => setDebouncedQ(q.trim()), 350);
        return () => clearTimeout(t);
    }, [q]);

    const buildQuery = (offset) => {
        const params = new URLSearchParams();
        params.set("limit", String(PAGE_SIZE));
        params.set("offset", String(offset));
        if (unreadOnly) params.set("unread_only", "true");
        if (eventFilter.length) params.set("event_type", eventFilter.join(","));
        if (severityFilter !== "all") params.set("severity", severityFilter);
        if (debouncedQ) params.set("q", debouncedQ);
        return params.toString();
    };

    // Reset & reload whenever filters change.
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        offsetRef.current = 0;
        api.get(`/notifications?${buildQuery(0)}`).then(r => {
            if (cancelled) return;
            setItems(r.data.items || []);
            setTotal(r.data.total || 0);
            setUnread(r.data.unread || 0);
            offsetRef.current = (r.data.items || []).length;
        }).finally(() => !cancelled && setLoading(false));
        return () => { cancelled = true; };
    }, [eventFilter, severityFilter, unreadOnly, debouncedQ]);

    const loadMore = async () => {
        if (loadingMore || items.length >= total) return;
        setLoadingMore(true);
        try {
            const r = await api.get(`/notifications?${buildQuery(offsetRef.current)}`);
            setItems(prev => [...prev, ...(r.data.items || [])]);
            offsetRef.current += (r.data.items || []).length;
        } finally {
            setLoadingMore(false);
        }
    };

    const markRead = async (id) => {
        try {
            await api.post(`/notifications/${id}/read`);
            setItems(prev => prev.map(it => it.notification_id === id ? { ...it, read: true } : it));
            setUnread(u => Math.max(0, u - 1));
        } catch {}
    };
    const markAllRead = async () => {
        try {
            await api.post("/notifications/read-all");
            setItems(prev => prev.map(it => ({ ...it, read: true })));
            setUnread(0);
        } catch {}
    };
    const deleteOne = async (id) => {
        try {
            await api.delete(`/notifications/${id}`);
            setItems(prev => prev.filter(it => it.notification_id !== id));
            setTotal(t => Math.max(0, t - 1));
        } catch {}
    };
    const openNotif = (n) => {
        if (!n.read) markRead(n.notification_id);
        if (n.action_url) navigate(n.action_url);
    };

    const toggleEvent = (et) => {
        setEventFilter(prev => prev.includes(et) ? prev.filter(x => x !== et) : [...prev, et]);
    };

    const filtersActive = eventFilter.length > 0 || severityFilter !== "all" || unreadOnly || !!debouncedQ;
    const eventsCatalog = meta?.events || [];

    return (
        <AppShell testid="notifications-page" containerClass="container-mx py-10 max-w-3xl">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                        <div>
                            <div className="text-xs uppercase tracking-wider font-mono text-primary-c">Історія</div>
                            <h1 className="mt-1 font-display font-extrabold text-text-primary"
                                style={{ fontSize: "clamp(32px, 4.5vw, 48px)", letterSpacing: "-0.02em" }}>
                                Сповіщення
                            </h1>
                            <p className="mt-2 text-sm text-secondary-c" data-testid="notifs-counter">
                                Всього: <span className="font-mono text-text-primary font-semibold">{total}</span>
                                {unread > 0 && <span className="ml-2">· непрочитаних: <span className="font-mono text-primary-c font-semibold">{unread}</span></span>}
                            </p>
                        </div>
                        {unread > 0 && (
                            <button
                                onClick={markAllRead}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border border-border bg-surface/30 hover:border-primary/30 text-text-primary"
                                data-testid="notifs-mark-all"
                            >
                                <Check size={14} /> Прочитати все
                            </button>
                        )}
                    </div>

                    {/* Search + Toggle filters */}
                    <div className="mt-6 flex items-center gap-3 flex-wrap">
                        <div className="relative flex-1 min-w-[200px]">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-faint-c" />
                            <input
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Пошук по заголовку та тексту…"
                                className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-border bg-surface/30 text-sm text-text-primary placeholder:text-faint-c focus:outline-none focus:border-primary/40"
                                data-testid="notifs-search"
                            />
                        </div>
                        <button
                            onClick={() => setShowFilters(v => !v)}
                            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition ${
                                filtersActive ? "border-primary/40 bg-primary/10 text-primary-c" : "border-border bg-surface/30 text-secondary-c hover:text-text-primary"
                            }`}
                            data-testid="notifs-toggle-filters"
                        >
                            <FilterIcon size={14} /> Фільтри{filtersActive ? ` (${(eventFilter.length || 0) + (severityFilter !== "all" ? 1 : 0) + (unreadOnly ? 1 : 0) + (debouncedQ ? 1 : 0)})` : ""}
                        </button>
                    </div>

                    {showFilters && (
                        <div className="mt-3 glass rounded-xl p-5 space-y-4" data-testid="notifs-filter-panel">
                            <FilterRow label="Тип події">
                                <div className="flex flex-wrap gap-2">
                                    {eventsCatalog.map(e => (
                                        <button
                                            key={e.event_type}
                                            onClick={() => toggleEvent(e.event_type)}
                                            data-testid={`notifs-filter-event-${e.event_type}`}
                                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition border ${
                                                eventFilter.includes(e.event_type)
                                                    ? "bg-primary/20 border-primary/50 text-primary-c"
                                                    : "bg-surface/30 border-border text-secondary-c hover:text-text-primary"}`}
                                        >{e.label}</button>
                                    ))}
                                </div>
                            </FilterRow>

                            <FilterRow label="Важливість">
                                <div className="flex flex-wrap gap-2">
                                    {[["all", "Усі"], ["critical", "Критично"], ["warning", "Попередження"], ["info", "Інфо"]].map(([val, lbl]) => (
                                        <button
                                            key={val}
                                            onClick={() => setSeverityFilter(val)}
                                            data-testid={`notifs-filter-sev-${val}`}
                                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition border ${
                                                severityFilter === val
                                                    ? "bg-primary/20 border-primary/50 text-primary-c"
                                                    : "bg-surface/30 border-border text-secondary-c hover:text-text-primary"}`}
                                        >{lbl}</button>
                                    ))}
                                </div>
                            </FilterRow>

                            <FilterRow label="Стан">
                                <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-text-primary">
                                    <input
                                        type="checkbox"
                                        checked={unreadOnly}
                                        onChange={(e) => setUnreadOnly(e.target.checked)}
                                        className="rounded border-border bg-surface text-primary focus:ring-primary"
                                        data-testid="notifs-filter-unread"
                                    />
                                    Тільки непрочитані
                                </label>
                            </FilterRow>

                            {filtersActive && (
                                <button
                                    onClick={() => { setEventFilter([]); setSeverityFilter("all"); setUnreadOnly(false); setQ(""); }}
                                    className="text-xs text-secondary-c hover:text-text-primary inline-flex items-center gap-1"
                                    data-testid="notifs-clear-filters"
                                >
                                    <X size={12} /> Скинути фільтри
                                </button>
                            )}
                        </div>
                    )}

                    <div className="mt-6">
                        {loading ? (
                            <ListSkeleton />
                        ) : items.length === 0 ? (
                            <EmptyState filtersActive={filtersActive} />
                        ) : (
                            <ul className="space-y-2" data-testid="notifs-items">
                                {items.map(n => (
                                    <NotificationRow
                                        key={n.notification_id}
                                        n={n}
                                        onOpen={() => openNotif(n)}
                                        onMarkRead={() => markRead(n.notification_id)}
                                        onDelete={() => deleteOne(n.notification_id)}
                                    />
                                ))}
                            </ul>
                        )}

                        {!loading && items.length < total && (
                            <div className="mt-6 text-center">
                                <button
                                    onClick={loadMore}
                                    disabled={loadingMore}
                                    className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-surface/40 border border-border text-text-primary hover:border-primary/30 disabled:opacity-50"
                                    data-testid="notifs-load-more"
                                >
                                    {loadingMore ? "Завантаження…" : `Показати ще (${total - items.length})`}
                                </button>
                            </div>
                        )}
                    </div>
        </AppShell>
    );
}

function FilterRow({ label, children }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider font-mono text-faint-c mb-2">{label}</div>
            {children}
        </div>
    );
}

function NotificationRow({ n, onOpen, onMarkRead, onDelete }) {
    const sev = SEVERITY[n.severity] || SEVERITY.info;
    const Icon = iconFor(n);
    return (
        <li
            data-testid={`notif-row-${n.notification_id}`}
            className={`glass rounded-xl p-4 transition border ${
                n.read ? "border-transparent hover:border-primary/20" : "border-primary/25 bg-primary/[0.03]"}`}
        >
            <div className="flex items-start gap-4">
                <div className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center border ${sev.bg} ${sev.border}`}>
                    <Icon size={18} className={sev.color} />
                </div>
                <div className="flex-1 min-w-0 cursor-pointer" onClick={onOpen}>
                    <div className="flex items-center gap-2 flex-wrap">
                        <h3 className={`font-medium text-sm ${sev.color} truncate`}>{n.title}</h3>
                        {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-primary-c shrink-0" />}
                        <span className={`text-[9px] uppercase font-mono tracking-wider px-1.5 py-0.5 rounded ${sev.bg} ${sev.color}`}>{sev.label}</span>
                    </div>
                    <p className="mt-1 text-sm text-text-primary/90 leading-snug">{n.message}</p>
                    {n.campaign_name && (
                        <div className="mt-1 text-[11px] font-mono text-faint-c">{n.campaign_name}</div>
                    )}
                    <div className="mt-2 flex items-center gap-3 text-[11px] font-mono text-faint-c">
                        <span>{formatRelative(n.created_at)}</span>
                        <span className="text-primary-c/70">{n.event_type || n.kind}</span>
                        {n.action_label && n.action_url && (
                            <span className="text-primary-c font-semibold ml-auto">{n.action_label} →</span>
                        )}
                    </div>
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                    {!n.read && (
                        <button
                            onClick={(e) => { e.stopPropagation(); onMarkRead(); }}
                            className="p-1.5 rounded text-secondary-c hover:text-text-primary hover:bg-surface-hover/40"
                            title="Прочитати"
                            data-testid={`notif-row-read-${n.notification_id}`}
                        >
                            <Check size={14} />
                        </button>
                    )}
                    <button
                        onClick={(e) => { e.stopPropagation(); onDelete(); }}
                        className="p-1.5 rounded text-secondary-c hover:text-error-c hover:bg-error/10"
                        title="Видалити"
                        data-testid={`notif-row-delete-${n.notification_id}`}
                    >
                        <Trash2 size={14} />
                    </button>
                </div>
            </div>
        </li>
    );
}

function ListSkeleton() {
    return (
        <ul className="space-y-2" data-testid="notifs-skeleton">
            {[0,1,2,3].map(i => (
                <li key={i} className="glass rounded-xl p-4 animate-pulse">
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-xl bg-surface-hover/40" />
                        <div className="flex-1">
                            <div className="h-3 bg-surface-hover/40 rounded w-1/3" />
                            <div className="mt-2 h-4 bg-surface-hover/40 rounded w-4/5" />
                        </div>
                    </div>
                </li>
            ))}
        </ul>
    );
}

function EmptyState({ filtersActive }) {
    return (
        <div className="glass rounded-3xl p-12 text-center" data-testid="notifs-empty">
            <div className="mx-auto w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mb-5">
                <Bell size={32} className="text-primary-c" />
            </div>
            <h2 className="font-display font-bold text-xl text-text-primary">
                {filtersActive ? "Нічого не знайдено" : "Поки що немає сповіщень"}
            </h2>
            <p className="mt-2 text-sm text-secondary-c max-w-md mx-auto">
                {filtersActive
                    ? "Жодне сповіщення не підходить під ваші фільтри. Спробуйте змінити їх."
                    : "Тут будуть всі ваші сповіщення коли вони з'являться — звіти, аномалії, нагадування і платежі."}
            </p>
        </div>
    );
}
